import { AnalyticsService } from '@alison/analytics/client';
import type { ToastState } from '@alison/ui';
import Box from '@mui/material/Box';
import type { QueryKey } from '@tanstack/react-query';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useCallback, useMemo, useState } from 'react';
import { useTRPC } from '@/lib/trpc.js';
import { ConfirmDialog } from './ConfirmDialog.js';
import { tableColumns } from './constants.js';
import { MemberRowView } from './MemberRowView.js';
import type { JobTitleId, MemberRow } from './types.js';

interface MembersTableProps {
  isAdmin: boolean;
  viewerIsAlisonStaff: boolean;
  onToast: (toast: ToastState) => void;
}

type PendingAction =
  | { kind: 'make-admin'; userId: number; name: string }
  | { kind: 'remove'; userId: number; name: string }
  | { kind: 'revoke'; inviteId: number; name: string };

export function MembersTable({
  isAdmin,
  viewerIsAlisonStaff,
  onToast,
}: MembersTableProps): ReactNode {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<PendingAction | null>(null);

  const usersQuery = useQuery(trpc.users.list.queryOptions());
  const invitesQuery = useQuery(trpc.invites.list.queryOptions());

  const invalidate = (queryKey: QueryKey): void => {
    void queryClient.invalidateQueries({ queryKey });
  };
  const inviteEmail = (inviteId: number): string =>
    (invitesQuery.data ?? []).find((entry) => entry.id === inviteId)?.email ?? '';

  const updateMutation = useMutation(
    trpc.users.update.mutationOptions({
      onSuccess: () => {
        invalidate(trpc.users.list.queryKey());
        invalidate(trpc.me.get.queryKey());
        setPending(null);
      },
    }),
  );
  const removeMutation = useMutation(
    trpc.users.remove.mutationOptions({
      onSuccess: () => {
        invalidate(trpc.users.list.queryKey());
        invalidate(trpc.invites.list.queryKey());
        setPending(null);
        onToast({ message: 'User removed', severity: 'error' });
      },
    }),
  );
  const revokeMutation = useMutation(
    trpc.invites.revoke.mutationOptions({
      onSuccess: (_data, variables) => {
        invalidate(trpc.invites.list.queryKey());
        setPending(null);
        AnalyticsService.track('Invite - Revoked', { invitedEmail: inviteEmail(variables.id) });
        onToast({ message: 'Invitation revoked', severity: 'warning' });
      },
    }),
  );
  const resendMutation = useMutation(
    trpc.invites.resend.mutationOptions({
      onSuccess: (_data, variables) => {
        invalidate(trpc.invites.list.queryKey());
        AnalyticsService.track('Invite - Resent', { invitedEmail: inviteEmail(variables.id) });
        onToast({ message: 'Invitation resent', severity: 'info' });
      },
      onError: (error) => {
        onToast({ message: error.message, severity: 'error' });
      },
    }),
  );

  const updateMutate = updateMutation.mutate;
  const onChangeJob = useCallback(
    (userId: number, job: JobTitleId | null) => updateMutate({ userId, job }),
    [updateMutate],
  );
  const onMakeAdmin = useCallback(
    (userId: number, name: string) => setPending({ kind: 'make-admin', userId, name }),
    [],
  );
  const onRemove = useCallback(
    (userId: number, name: string) => setPending({ kind: 'remove', userId, name }),
    [],
  );
  const onRevoke = useCallback(
    (inviteId: number, name: string) => setPending({ kind: 'revoke', inviteId, name }),
    [],
  );
  const resendMutate = resendMutation.mutate;
  const onResend = useCallback(
    (inviteId: number) => resendMutate({ id: inviteId }),
    [resendMutate],
  );

  const confirmDialog =
    pending === null
      ? null
      : pending.kind === 'make-admin'
        ? {
            title: 'Transfer admin access?',
            danger: false,
            confirmLabel: 'Make admin',
            isPending: updateMutation.isPending,
            onConfirm: () => updateMutation.mutate({ userId: pending.userId, role: 'admin' }),
            message: (
              <>
                Each workspace has a single admin. Making <strong>{pending.name}</strong> the admin
                will demote you to a member. You'll no longer be able to invite people, change
                roles, or edit job titles.
              </>
            ),
          }
        : pending.kind === 'remove'
          ? {
              title: 'Remove this user?',
              danger: true,
              confirmLabel: 'Remove user',
              isPending: removeMutation.isPending,
              onConfirm: () => removeMutation.mutate({ userId: pending.userId }),
              message: (
                <>
                  Removing <strong>{pending.name}</strong> revokes their access to this workspace.
                  They would need a new invitation to return.
                </>
              ),
            }
          : {
              title: 'Revoke this invitation?',
              danger: true,
              confirmLabel: 'Revoke invite',
              isPending: revokeMutation.isPending,
              onConfirm: () => revokeMutation.mutate({ id: pending.inviteId }),
              message: (
                <>
                  Revoking the invitation for <strong>{pending.name}</strong> immediately
                  invalidates their invite link.
                </>
              ),
            };

  const rows = useMemo<MemberRow[]>(() => {
    const memberRows: MemberRow[] = (usersQuery.data ?? []).map((user) => ({
      key: `user-${user.id}`,
      name: user.name,
      email: user.email,
      job: user.job,
      role: user.role,
      status: 'active',
      userId: user.id,
      inviteId: null,
      isAlisonStaff: user.isAlisonStaff,
      organizationName: user.organizationName ?? null,
      canEdit: user.canEdit,
    }));
    const inviteRows: MemberRow[] = (invitesQuery.data ?? []).map((invite) => ({
      key: `invite-${invite.id}`,
      name: invite.name ?? invite.email,
      email: invite.email,
      job: invite.job,
      role: invite.role,
      status: 'invited',
      userId: null,
      inviteId: invite.id,
      isAlisonStaff: invite.isAlisonStaff,
      organizationName: invite.organizationName ?? null,
      canEdit: true,
    }));
    const allRows = [...memberRows, ...inviteRows];
    return [
      ...allRows.filter((row) => !row.isAlisonStaff),
      ...allRows.filter((row) => row.isAlisonStaff),
    ];
  }, [usersQuery.data, invitesQuery.data]);

  const lastKey = rows.at(-1)?.key ?? null;
  // Only a client linked to several orgs needs each row to say whose member it is.
  const showOrganization =
    new Set(rows.map((row) => row.organizationName).filter((name) => name !== null)).size > 1;

  const renderRow = (row: MemberRow): ReactNode => (
    <MemberRowView
      key={row.key}
      row={row}
      isLast={row.key === lastKey}
      isAdmin={isAdmin}
      viewerIsAlisonStaff={viewerIsAlisonStaff}
      showOrganization={showOrganization}
      onChangeJob={onChangeJob}
      onMakeAdmin={onMakeAdmin}
      onRemove={onRemove}
      onRevoke={onRevoke}
      onResend={onResend}
    />
  );

  return (
    <Box
      data-testid="table-members"
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1.5,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: tableColumns,
          gap: 2,
          py: 2,
          px: 3,
          bgcolor: 'background.page',
          borderBottom: '1px solid',
          borderColor: 'divider',
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          color: 'text.disabled',
        }}
      >
        <Box component="span">User</Box>
        <Box component="span">Job title</Box>
        <Box component="span">Role</Box>
        <Box component="span">Status</Box>
        <Box component="span" sx={{ textAlign: 'right' }}>
          Actions
        </Box>
      </Box>
      {rows.map(renderRow)}

      {confirmDialog !== null ? (
        <ConfirmDialog
          open
          title={confirmDialog.title}
          message={confirmDialog.message}
          confirmLabel={confirmDialog.confirmLabel}
          danger={confirmDialog.danger}
          isPending={confirmDialog.isPending}
          onCancel={() => setPending(null)}
          onConfirm={confirmDialog.onConfirm}
        />
      ) : null}
    </Box>
  );
}
